"use client";

import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Categories, Tags, Pages, CmsBannerDetails } from "@/interface/types";
import axios from "axios";
import { Button } from "@/components/ui/button";
import DOMPurify from "dompurify";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";

const Cards = () => {
  const [isPopupOpen, setPopupOpen] = useState({ open: false, code: "" });
  const [categories, setCategories] = useState<Categories[]>([]);
  const [tags, setTags] = useState<{ [key: string]: Tags[] }>({});
  const [bannerDetails, setBannerDetails] = useState<CmsBannerDetails | null>(
    null,
  );

  const params = useParams();
  const router = useRouter();
  const currentCategory = params.categoryName as string;

  const closePopup = () => {
    setPopupOpen({ open: false, code: "" });
  };

  const getCategories = async () => {
    try {
      const API_URL =
        "https://website-cms-backend-prod-154625965269.asia-south1.run.app/categories/?vertical=Accounting";
      const response = await axios.get(API_URL);
      const { status, data } = response;
      if (status === 200) {
        setCategories(data.Data);
        fetchTagsForCategories(data.Data);
      } else {
        console.warn("empty array");
      }
    } catch (error) {
      console.error("failed to fetch categories");
    }
  };

  const getTags = async (category: string) => {
    if (!category) {
      console.error("Category is required");
      return;
    }

    try {
      const API_URL = `https://website-cms-backend-prod-154625965269.asia-south1.run.app/tags?category=${category}`;
      const response = await axios.get(API_URL);
      const { status, data } = response;
      if (status === 200 && data.Data.length > 0) {
        return data.Data;
      } else {
        return [];
      }
    } catch (error) {
      console.error(`Failed to fetch tags for category: ${category}`);
      return [];
    }
  };

  const fetchBannerDetails = async () => {
    try {
      const response = await axios.get(
        "https://website-cms-backend-prod-154625965269.asia-south1.run.app/homepage?vertical=Accounting",
      );
      const { status, data } = response;
      if (status === 200) {
        setBannerDetails(data);
      } else {
        console.warn("Data is not found");
      }
    } catch (error) {
      console.error("Failed to fetch banner details");
    }
  };

  const fetchTagsForCategories = async (categories: Categories[]) => {
    const tagsData: { [key: string]: Tags[] } = {};
    for (const category of categories) {
      if (!category.slug) {
        console.warn("Skipping category with missing slug:", category);
        continue;
      }

      const fetchedTags = await getTags(category.slug);
      tagsData[category.slug] = fetchedTags;
    }
    setTags(tagsData);
  };

  const handleNavigation = (
    categorySlug: string,
    tagSlug: string,
    pageSlug: string,
  ) => {
    const url = `/accounting/learn/${categorySlug}/${tagSlug}/${pageSlug}`;
    const fullUrl = `${window.location.origin}${url}`;
    window.open(fullUrl, "_blank");
  };

  const sanitizedSideBannerHTML = bannerDetails?.side_content
    ? DOMPurify.sanitize(bannerDetails?.side_content)
    : "";

  const renderSideBannerContent = () => {
    if (!bannerDetails || bannerDetails.side_content === "<p>&nbsp;</p>") {
      return (
        <div className="text-center text-3xl text-gray-500 font-bold capitalize p-8">
          No Content available
        </div>
      );
    }

    return (
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: sanitizedSideBannerHTML }}
      />
    );
  };

  const getYouTubeEmbedURL = (url: string) => {
    const videoId = url.split("v=")[1]?.split("&")[0];
    return `https://www.youtube.com/embed/${videoId}`;
  };

  useEffect(() => {
    getCategories();
    fetchBannerDetails();
  }, []);

  useEffect(() => {
    if (
      currentCategory &&
      categories.length > 0 &&
      !categories.some((cat) => cat.slug === currentCategory)
    ) {
      router.push("/accounting/learn");
    }
  }, [currentCategory, categories, router]);

  return (
    <>
      <div className="mx-auto w-full xl:px-0 md:px-6 lg:!px-6  px-5 relative md:bg-bottom bg-center bg-[url(/home/footer_bg.png)] bg-repeat md:bg-cover bg-inherit bg-[length:100% 100%] md:pb-0 pb-[0px]">
        <div className="lg:container mx-auto 2xl:px-0 xl:px-10 lg:p-0 text-[#030F1C] z-20 relative">
          <div className="flex justify-between">
            <div className="md:w-[72%] w-full md:justify-start justify-center gap-9">
              {categories.length > 0
                ? categories.map((category) => {
                    return (
                      <div key={category.id} id={`category-${category.name}`}>
                        <div className="md:text-left text-center">
                          <h2 className="2xl:text-[36px] md:text-[32px] text-[20px] font-bold border-b-[1px] border-[#FFBA08] inline-block mt-8">
                            {category.name}
                          </h2>
                        </div>
                        <div className="flex justify-between flex-wrap items-start border-b-[1px] border-black pb-12">
                          <div className=" w-full flex md:justify-start justify-center flex-wrap gap-9">
                            {tags[category.slug as keyof typeof tags]
                              ?.length ? (
                              tags[category.slug as keyof typeof tags]?.map(
                                (tag) => (
                                  <div
                                    key={tag.id}
                                    className="cms_card_sha md:w-[30%] w-[328px] md:rounded-[4px] rounded-[10px] mt-6 h-[380px]"
                                  >
                                    <div className="text-center md:rounded-t-[4px] rounded-t-[10px] 2xl:text-[18px] text-[14px] font-semibold py-4 card_head text-white">
                                      {tag.name}
                                    </div>
                                    <div className="py-2 px-4">
                                      {tag.pages.length > 0 ? (
                                        <ul className="overflow-y-auto h-[310px]">
                                          {tag.pages.map(
                                            (page: Pages, index: number) => (
                                              <li key={index}>
                                                <Button
                                                  className="border-b-[0.5px] border-[#3C86F1] flex justify-between items-center px-3 text-[#3C86F1] pt-2 pb-1 bg-transparent shadow-none w-full rounded-none hover:bg-transparent"
                                                  onClick={() =>
                                                    handleNavigation(
                                                      category.slug,
                                                      tag.slug,
                                                      page.slug,
                                                    )
                                                  }
                                                >
                                                  <span className="lg:tet-[16px] 2xl:text-[16px] md:text-[14px] font-medium text-left">
                                                    {page.name}
                                                  </span>
                                                  <Image
                                                    priority
                                                    src="/cms/arrow.svg"
                                                    alt=""
                                                    className="h-9 w-9"
                                                    width={35}
                                                    height={35}
                                                  />
                                                </Button>
                                              </li>
                                            ),
                                          )}
                                        </ul>
                                      ) : null}
                                    </div>
                                  </div>
                                ),
                              )
                            ) : (
                              <div className="text-gray-500">
                                No tags available
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : null}
            </div>
            {bannerDetails ? (
              <div className="md:w-[24.5%] w-full md:block hidden mt-20">
                {bannerDetails.side_video ? (
                  <div className="rounded-[10px] mt-6 relative cursor-pointer">
                    <iframe
                      height="170"
                      src={getYouTubeEmbedURL(bannerDetails.side_video)}
                      title="video"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      className="rounded-[4px]"
                    />
                  </div>
                ) : bannerDetails.side_banner ? (
                  <div className="rounded-[10px] mt-6 relative cursor-pointer w-[298px]">
                    <Link href={bannerDetails.side_banner_link}>
                      <Image
                        src={bannerDetails.side_banner}
                        alt="MBE"
                        className="rounded-[4px]"
                        width={298}
                        height={170}
                      />
                    </Link>
                  </div>
                ) : null}

                <div className="cms_card_sha px-3 py-3 mt-6 rounded-[10px]">
                  <p className="text-base font-bold leading-[1.4] border-b-[0.5px] border-[#959595] pb-2 capitalize">
                    {bannerDetails.side_title}
                  </p>
                  <div className="cms_side_banner">
                    {renderSideBannerContent()}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {isPopupOpen.open && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white p-1 shadow-lg relative rounded-[14px] md:w-[auto] w-[85%]">
              <button
                onClick={closePopup}
                className="mt-4 bg-red-700 text-white px-3 py-1 rounded absolute right-[-15px] top-[-30px]"
              >
                ✕
              </button>
              <iframe
                className="lg:w-[600px] md:w-[500px] md:h-[280px] rounded-[14px] lg:h-[320px] h-[220px]"
                width="100%"
                height="320"
                src={`https://www.youtube.com/embed/${isPopupOpen.code}`}
                title="YouTube video player"
                allow="accelerometer; autoplay; climd:oard- pb-14write; encrypted-media; gyroscope; picture-in-picture; web-share"
              ></iframe>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Cards;
